/**
 * Lookup Service
 * Pure functions for phone-based document lookup
 */

import apiClient from "../api/client";

const LOOKUP_TOKEN_KEY = "lookup_token";

/**
 * Save lookup JWT token to session storage
 * @param {string} token - JWT token
 */
export const setLookupToken = (token) => {
  if (token) {
    sessionStorage.setItem(LOOKUP_TOKEN_KEY, token);
  } else {
    sessionStorage.removeItem(LOOKUP_TOKEN_KEY);
  }
};

/**
 * Get lookup JWT token from session storage
 * @returns {string|null} JWT token or null
 */
export const getLookupToken = () => {
  return sessionStorage.getItem(LOOKUP_TOKEN_KEY);
};

/**
 * Clear lookup JWT token from session storage
 */
export const clearLookupToken = () => {
  sessionStorage.removeItem(LOOKUP_TOKEN_KEY);
};

/**
 * Check if user has a valid lookup token
 * @returns {boolean} True if token exists
 */
export const hasLookupToken = () => {
  return !!getLookupToken();
};

/**
 * Send OTP for document lookup
 * @param {string} phone - Phone number
 * @returns {Promise<{data: {sent: boolean, expiresAt: string, contractsCount: number, _devOtp?: string}}>}
 */
export const sendLookupOtp = async (phone) => {
  return apiClient.post(
    "/public/lookup/otp/send",
    { phone },
    { requiresAuth: false }
  );
};

/**
 * Verify OTP for document lookup
 * @param {string} phone - Phone number
 * @param {string} code - OTP code
 * @returns {Promise<{data: {verified: boolean, token: string}}>}
 */
export const verifyLookupOtp = async (phone, code) => {
  return apiClient.post(
    "/public/lookup/otp/verify",
    { phone, code },
    { requiresAuth: false }
  );
};

/**
 * Get list of available contracts for verified phone
 * @returns {Promise<{data: {contracts: Array, total: number}}>}
 */
export const getMyContracts = async () => {
  const token = getLookupToken();
  return apiClient.get("/public/lookup/contracts", {
    requiresAuth: false, // Don't use default auth token
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get public token for a specific contract
 * @param {string} contractId - Contract ID
 * @returns {Promise<{data: {token: string}}>}
 */
export const getContractToken = async (contractId) => {
  const token = getLookupToken();
  return apiClient.get(`/public/lookup/contracts/${contractId}/token`, {
    requiresAuth: false, // Don't use default auth token
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get lookup token from existing signing session (no OTP needed)
 * Requires valid signing JWT in session storage
 * @returns {Promise<{data: {token: string, phone: string}}>}
 */
export const getLookupFromSession = async () => {
  return apiClient.post("/public/lookup/from-session", {});
};
